/**
 * Stash and restore the post-sign-in `returnTo` path.
 *
 * We do NOT persist this in localStorage / sessionStorage (per ADR-014).
 * Instead, the path is piggy-backed on MSAL's `state` parameter, which is
 * a server-side OIDC primitive (round-tripped through Entra and returned
 * intact on the `/sign-in/callback` redirect). MSAL preserves anything we
 * pass as the `state` field on `loginRedirect({ state })`; on callback we
 * read it back via `handleRedirectPromise()` -> `AuthenticationResult.state`.
 *
 * The path is validated before navigation: only same-origin relative paths
 * starting with a single `/` are honoured, to avoid open-redirect bugs.
 */

/** Serialise a returnTo path into the OIDC `state` parameter. */
export function encodeReturnTo(path: string | undefined): string {
  if (!path) return ''
  return JSON.stringify({ returnTo: path })
}

/** Read a returnTo path from the OIDC `state` parameter. */
export function decodeReturnTo(state: string | null | undefined): string | null {
  if (!state) return null
  try {
    const parsed = JSON.parse(state) as { returnTo?: unknown }
    if (typeof parsed.returnTo !== 'string') return null
    return sanitiseReturnTo(parsed.returnTo)
  } catch {
    return null
  }
}

/**
 * Sanitise a returnTo path: must start with a single `/`, must not start
 * with `//` (protocol-relative URL), must not contain `://`. Anything else
 * is rejected. This is the same-origin-only invariant.
 */
export function sanitiseReturnTo(path: string): string | null {
  if (typeof path !== 'string') return null
  if (path.length === 0) return null
  if (!path.startsWith('/')) return null
  if (path.startsWith('//')) return null
  if (path.includes('://')) return null
  return path
}
